import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import analyticsService from '../services/analyticsService';
import { useFittedDropdownWidth } from '../hooks/useFittedDropdownWidth';

// Custom dropdown component for filtering open houses by day of the week
const DayFilterDropdown = ({ value, options, onChange, className = '', viewContext = 'Unknown' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Blur button when dropdown closes
  useEffect(() => {
    if (!isOpen && buttonRef.current) {
      buttonRef.current.blur();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  const optionLabels = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => opt?.label ?? '').filter(Boolean);
  }, [options]);

  const controlWidthPx = useFittedDropdownWidth({
    buttonRef,
    labels: optionLabels,
    minPx: 0,
    maxPx: 360,
    viewportPaddingPx: 32,
    bufferPx: 8,
    iconMarginLeftPx: 12,
    fallbackIconWidthPx: 14
  });

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }} className={className}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px 8px 12px',
          borderRadius: '8px',
          border: '1px solid #ccc',
          background: '#fff',
          color: '#111',
          fontWeight: 600,
          fontSize: '16px',
          cursor: 'pointer',
          height: '40px',
          width: controlWidthPx ? `${controlWidthPx}px` : 'auto',
          maxWidth: '100%',
          textAlign: 'left',
          outline: 'none'
        }}
      >
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0
          }}
        >
          {selectedOption.label}
        </span>
        <FaChevronDown style={{ marginLeft: '12px', fontSize: '14px', color: '#666', flexShrink: 0 }} />
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '8px',
          marginTop: '4px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {options.map(option => {
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                onClick={() => {
                  // Only call onChange if selecting a different option
                  if (!isSelected) {
                    onChange({ target: { value: option.value } });
                    // Track filter usage with view context
                    analyticsService.trackEvent({
                      category: 'Filter',
                      action: 'Day Filter',
                      label: option.label,
                      value: 1,
                      params: {
                        filter_value: option.label,
                        view_context: viewContext
                      }
                    });
                  }
                  // Always close the dropdown
                  setIsOpen(false);
                }}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#f5f5f5' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  // Only highlight on hover if not already selected
                  if (!isSelected) {
                    e.target.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  // Reset to transparent or selected background
                  e.target.style.backgroundColor = isSelected ? '#f5f5f5' : 'transparent';
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DayFilterDropdown;
